import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { projectKeys } from '../projects/hooks';
import { createSection, renameSection, reorderSections, setSectionArchived } from './queries';
import type { CreateSectionInput } from './types';

/**
 * Section mutations. Sections arrive with the project workspace query, so
 * every mutation invalidates that one key rather than owning a key of its own.
 * Nothing is written optimistically: the database decides who may manage
 * sections, and a refused write must not flicker in the interface first.
 */

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useCreateSection(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: CreateSectionInput) => createSection(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useRenameSection(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, name }: { sectionId: string; name: string }) =>
      renameSection(client, sectionId, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useSetSectionArchived(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, isArchived }: { sectionId: string; isArchived: boolean }) =>
      setSectionArchived(client, sectionId, isArchived),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export function useReorderSections(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: { id: string; position: number }[]) => reorderSections(client, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}
